import { useState, useEffect } from "react";
import { Settings, RefreshCw } from "lucide-react";
import { selectSiteHeaderColor } from "../../../AsianExchangeSrc/utils/helper/commonSelectors";
import { useSelector } from "react-redux";
import { themeColors } from "../../utils/themeColors";
import { usePrimaryColor, useSecondaryColor, useSecondaryTextColor } from "../../hooks/useThemeColors";
import { useNavigate } from "react-router-dom";



export default function SettingsPage() {
  const [selectedTheme, setSelectedTheme] = useState(() => {
    const savedTheme = localStorage.getItem("themeData");
    return savedTheme ? JSON.parse(savedTheme) : themeColors.find(t => t.id === "theme1");
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") return true;
    if (savedTheme === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const headerColor = useSelector(selectSiteHeaderColor());
      const SecondaryTextColor = useSecondaryTextColor()


  useEffect(() => {
    localStorage.setItem("themeData", JSON.stringify(selectedTheme));
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
    window.dispatchEvent(new Event("themeChanged"));
    document.documentElement.classList.toggle("dark", isDarkMode);

  }, [selectedTheme, isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [stakes, setStakes] = useState([
    { id: 1, buttonValue: "100", newValue: "100" },
    { id: 2, buttonValue: "200", newValue: "200" },
    { id: 3, buttonValue: "500", newValue: "500" },
    { id: 4, buttonValue: "1000", newValue: "1000" },
    { id: 5, buttonValue: "2000", newValue: "2000" },
    { id: 6, buttonValue: "5000", newValue: "5000" },
    { id: 7, buttonValue: "10000", newValue: "10000" },
    { id: 8, buttonValue: "25000", newValue: "25000" },
  ]);

  useEffect(() => {
    const savedStakes = localStorage.getItem("stakes");

    if (savedStakes) {
      try {
        const parsedStakes = JSON.parse(savedStakes);
        if (Array.isArray(parsedStakes)) {
          setStakes(parsedStakes);
        }
      } catch (err) {
        console.error("Failed to parse saved stakes:", err);
      }
    }
  }, []);

  const handleInputChange = (id, value) => {
    const updatedStakes = stakes.map((stake) =>
      stake.id === id ? { ...stake, newValue: value } : stake
    );
    setStakes(updatedStakes);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setLoading(true);
    setTimeout(() => {
      try {
        const updatedStakes = stakes.map((stake) => ({
          ...stake,
          buttonValue: stake.newValue,
        }));
        setStakes(updatedStakes);
        localStorage.setItem("stakes", JSON.stringify(updatedStakes));
        toast.success("Stake values saved successfully!");
        navigate("/");
      } catch (error) {
        toast.error("Failed to save stake values");
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  return (
    <div className="bg-light-bg dark:bg-dark-bg text-gray-800 min-h-screen">
      {/* Header */}
    

    {/* <div className="bg-[#EDEDED]"> */}
      
  <div className={`flex items-center justify-start p-2.5 shadow-md `} style={{ background: usePrimaryColor() }}>
        <Settings className={`mr-2 h-6 w-6 text-[${SecondaryTextColor}]`} />
          <h1 className={`text-md font-extrabold text-[${SecondaryTextColor}]`}>Setting</h1>
      </div>
      <div className="flex-1 overflow-y-auto pb-3">
        <div className="mb-4">
          <div className="flex items-center justify-between bg-[#2c3e50] p-1.5">
            <h2 className="text-[15px] font-extrabold text-white">Theme</h2>
            <button
              className="flex items-center space-x-1 rounded-md text-white px-3 text-sm font-medium shadow-sm"
              onClick={() => {
                setSelectedTheme("theme1");
                toast.info("Theme reset to default");
              }}
            >
              <span className="font-extrabold">Default</span>
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          <div className="bg-white p-4 dark:bg-dark-bg">
            <div className="grid grid-cols-5 gap-4 place-content-center place-items-center">
              {themeColors.map((theme) => (
                <button
                  key={theme.id}
                  className={`relative flex h-12 w-12 items-center justify-center rounded-full border-2 border-black transition-all duration-200 ${
                    selectedTheme === theme.id
                      ? "ring-2 ring-offset-2 ring-blue-500"
                      : ""
                  }`}
                  onClick={() => setSelectedTheme(theme)}
                  aria-label={`Select ${theme.id} theme`}
                >
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `linear-gradient(180deg, ${theme.gradient.start} 49%, ${theme.gradient.middle} 0, ${theme.gradient.middle} 51%, ${theme.gradient.end} 0)`,
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Dark/Light Mode Toggle Section */}
        <div className="mb-4">
          <div className="flex items-center justify-between bg-[#2c3e50] p-1.5">
            <h2 className="text-sm font-extrabold text-white">Dark/Light Mode</h2>
          </div>
          <div className="bg-white p-4 dark:bg-dark-bg">
            <label className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-800 dark:text-white">Toggle Dark Mode</span>
              <button
                onClick={toggleDarkMode}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                  isDarkMode ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
                    isDarkMode ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
            </label>
          </div>
        </div>

        {/* Stake Section */}
        <div className="mb-0">
          <div className="flex items-center bg-[#2c3e50] p-1.5">
            <h2 className="text-[15px] font-semibold text-white">Stake</h2>
          </div>
          <div className="bg-white p-1">
            <h3 className="mb-3 mt-2 text-sm font-extrabold text-gray-800">
              Quick Stakes
            </h3>
            <div className="grid grid-cols-4 gap-2">
              {stakes.map((stake) => (
                <input
                  key={stake.id}
                  value={stake.newValue}
                  type="text"
                  onChange={(e) => handleInputChange(stake.id, e?.target.value)}
                  className="border-[3px] border-solid text-[13px] p-1.5 text-center 
                  [border-image:conic-gradient(#6ca7ec,#2222b9,#6ca7ec,#2222b9,#6ca7ec,#2222b9,#6ca7ec)_1]
                  focus:outline-none focus:shadow-none focus:rounded-none focus:bg-[white]"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="p-4">
        <button
          className="w-full rounded-lg text-xl font-extrabold text-white"
          onClick={handleSubmit}
          style={{
            background: useSecondaryColor(),
            height: "56px",
          }}
        >
          {loading ? (
            <svg
              className="animate-spin h-5 w-5 mr-2 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          ) : null}
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}
